import _, {noop} from 'lodash';
import React, {FC, useMemo} from 'react';
import styled, {css} from 'styled-components';

import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {FrameViewerState} from '../../../../srcCommon/inboxes/side/sideFrameActions';
import {FrameTypesEnum} from '../../../models/api/apiTypes';
import {ApplicationEntryPointActionSdkVersionsEnum} from '../../../models/api/applicationEntryPointActionModel';
import {ApplicationEntryPointModel} from '../../../models/api/applicationEntryPointModel';
import {ConversationBaseModel, ConversationModel} from '../../../models/api/conversationModel';
import {IntegrationModel} from '../../../models/api/integrationModel';
import {PluginModel} from '../../../models/api/pluginModel';
import {
  ApplicationPanelLocationsEnum,
  ApplicationsPanelState,
} from '../../../store/panels/applications/applicationPanelState';
import {CellsContext} from '../../../store/panels/cells/context/cellsContext';
import {ApplicationsPanelId, RComposerPanelId, ViewerPanelId} from '../../../store/panels/panelStateBase';
import {NonNativeSidePanelFrame, SidePanelFrame} from '../../../store/stateless/sidePanelFrameSelectors';
import {IntegrationFrameContainer} from '../../inboxes/side/integrationFrameContainer';
import {PluginFrameContainer} from '../../inboxes/side/pluginFrameContainer';
import {SidePanelHeader} from '../../inboxes/side/sidePanelHeader';
import {AppEntrypointLegacySdkContainer} from '../appEntryPointLegacySdkContainer';
import {ConversationsApplicationContextHostContainer} from '../contextHosts/conversationsApplicationContextHostContainer';
import {LegacyConversationsApplicationContextHostContainer} from '../legacyContextHosts/legacyConversationsApplicationContextHostContainer';

/*
 * Props.
 */

interface PopupApplicationProps {
  panelId: ApplicationsPanelId;
  viewerPanelId: ViewerPanelId;
  frame: NonNativeSidePanelFrame;
  frames: ReadonlyArray<SidePanelFrame>;
  hasIntegrationFrames: boolean;
  viewerState?: FrameViewerState;
  onlyConversation: ConversationModel | undefined;
  selectedConversations: ReadonlyArray<ConversationBaseModel>;
  cellsContext: CellsContext;
  applicationsPanel: ApplicationsPanelState;
  onRequestClose: Handler;
}

/*
 * Style.
 */

const StyledContentWrapperDiv = styled.div`
  min-height: 0;
  height: 100%;
  position: relative;
  background-color: ${(p) => p.theme.foreground};
`;

interface StyledContentDivProps {
  $shouldHideHeader?: boolean;
}

const StyledContentDiv = styled.div<StyledContentDivProps>`
  min-height: 0;
  height: 100%;

  display: grid;
  grid-template-rows: auto 1fr;
  grid-auto-flow: row;

  ${(p) =>
    p.$shouldHideHeader &&
    css`
      grid-template-rows: 1fr;
    `};
`;

const StyledFramesDiv = styled.div`
  position: relative;
`;

/*
 * Component.
 */

export const PopupApplication: FC<PopupApplicationProps> = (props) => {
  const {frame, frames, applicationsPanel, onRequestClose} = props;
  const shouldHideHeader = applicationsPanel?.location === ApplicationPanelLocationsEnum.POPOVER;

  const plugins = useMemo(
    () =>
      _(frames)
        .map((f) => f.frameType === FrameTypesEnum.PLUGIN && f)
        .compact()
        .value(),
    [frames],
  );
  const integrations = useMemo(
    () =>
      _(frames)
        .map((f) => f.frameType === FrameTypesEnum.INTEGRATION && f)
        .compact()
        .value(),
    [frames],
  );
  const applicationEntryPointsLegacySdk = useMemo(
    () =>
      _(frames)
        .map(
          (f) =>
            f.frameType === FrameTypesEnum.APPLICATION_ENTRY_POINT &&
            f.action?.sdkVersion === ApplicationEntryPointActionSdkVersionsEnum.LEGACY &&
            f,
        )
        .compact()
        .value(),
    [frames],
  );

  return (
    <StyledContentWrapperDiv>
      <StyledContentDiv $shouldHideHeader={shouldHideHeader}>
        {!shouldHideHeader && <SidePanelHeader title={frame.name} onClose={onRequestClose} />}
        <StyledFramesDiv>
          {renderPlugins(props, plugins, frame)}
          {renderPluginIntegrations(props, integrations, frame)}
          {renderIntegrations(props, frame)}
          {renderLegacyEntryPoint(props, frame)}
          {renderApplicationEntryPointPluginSdk(props, frame)}
          {renderApplicationEntryPointsLegacySdk(props, frame, applicationEntryPointsLegacySdk)}
        </StyledFramesDiv>
      </StyledContentDiv>
    </StyledContentWrapperDiv>
  );
};

/*
 * Helpers.
 */

function renderPlugins(
  props: PopupApplicationProps,
  plugins: ReadonlyArray<PluginModel>,
  selectedFrame: NonNativeSidePanelFrame,
) {
  return plugins
    .filter((plugin) => selectedFrame === plugin)
    .map((plugin) => (
      <PluginFrameContainer
        key={plugin.id}
        panelId={props.viewerPanelId}
        plugin={plugin}
        isVisible
        viewerState={props.viewerState}
        onRequestWidthChange={noop}
      />
    ));
}

function renderPluginIntegrations(
  props: PopupApplicationProps,
  integrations: ReadonlyArray<IntegrationModel>,
  selectedFrame: NonNativeSidePanelFrame,
) {
  return _(integrations)
    .map((integration) => {
      const {plugin} = integration;
      if (!plugin) {
        return null;
      }

      return (
        <PluginFrameContainer
          key={integration.id}
          panelId={props.viewerPanelId}
          plugin={plugin}
          isVisible={selectedFrame === integration}
          viewerState={props.viewerState}
          onRequestWidthChange={noop}
        />
      );
    })
    .compact()
    .value();
}

function renderIntegrations(props: PopupApplicationProps, selectedFrame: NonNativeSidePanelFrame) {
  // Only create the iframe if we have at least one integration.
  if (!props.hasIntegrationFrames || selectedFrame.frameType !== FrameTypesEnum.INTEGRATION) {
    return null;
  }

  return (
    <IntegrationFrameContainer
      panelId={props.viewerPanelId}
      isVisible={isIntegrationFrameModel(selectedFrame)}
      integration={selectedFrame}
      viewerState={props.viewerState}
      isInPopup
    />
  );
}

function renderLegacyEntryPoint(props: PopupApplicationProps, selectedFrame: NonNativeSidePanelFrame) {
  if (!selectedFrame || selectedFrame.frameType !== FrameTypesEnum.ENTRY_POINT) {
    return null;
  }

  const {onlyConversation, selectedConversations, viewerPanelId, cellsContext} = props;

  return (
    <LegacyConversationsApplicationContextHostContainer
      entryPoint={selectedFrame}
      onlyConversation={onlyConversation}
      conversations={selectedConversations}
      panelId={viewerPanelId}
      cellsContext={cellsContext}
    />
  );
}

function renderApplicationEntryPointPluginSdk(
  props: PopupApplicationProps,
  selectedFrame: NonNativeSidePanelFrame,
) {
  if (
    !selectedFrame ||
    selectedFrame.frameType !== FrameTypesEnum.APPLICATION_ENTRY_POINT ||
    !selectedFrame.action ||
    selectedFrame.action.sdkVersion === ApplicationEntryPointActionSdkVersionsEnum.LEGACY
  ) {
    return null;
  }

  const {onlyConversation, selectedConversations, viewerPanelId, cellsContext, applicationsPanel} = props;
  const {parentPanelId} = applicationsPanel;
  return (
    <ConversationsApplicationContextHostContainer
      entryPoint={selectedFrame}
      onlyConversation={onlyConversation}
      conversations={selectedConversations}
      panelId={viewerPanelId}
      cellsContext={cellsContext}
      // If the parent panel is a composer panel, we are in a popped out window. This means we
      // may not have any conversations/conversation context so we will update the context type
      // to 'noConversationPopover'.
      composerPanelId={parentPanelId && RComposerPanelId.is(parentPanelId) ? parentPanelId : undefined}
      applicationsPanelId={
        applicationsPanel.location === ApplicationPanelLocationsEnum.POPOVER
          ? applicationsPanel.id
          : undefined
      }
    />
  );
}

function renderApplicationEntryPointsLegacySdk(
  props: PopupApplicationProps,
  selectedFrame: NonNativeSidePanelFrame,
  entryPoints: ReadonlyArray<ApplicationEntryPointModel>,
) {
  return entryPoints.map((entryPoint) => (
    <AppEntrypointLegacySdkContainer
      key={entryPoint.applicationId}
      entryPoint={entryPoint}
      panelId={props.viewerPanelId}
      viewerState={props.viewerState}
      isVisible={selectedFrame === entryPoint}
      onRequestWidthChange={noop}
    />
  ));
}

function isIntegrationFrameModel(frame?: NonNativeSidePanelFrame): frame is IntegrationModel {
  return Boolean(frame && frame.frameType === FrameTypesEnum.INTEGRATION && !frame.plugin);
}
